import { RefreshCw } from "lucide-react";
import {
  stateLabels,
  type RequestList,
  type RequestSummary,
} from "../../../../shared/labels/contracts";
import { SystemState } from "../../../shared/components/SystemState";
import type { LoadState, UiFault } from "../ui-types";

type CompletedSalesListProps = {
  items: RequestSummary[];
  list: RequestList | null;
  loadState: LoadState;
  fault: UiFault | null;
  onSelect: (id: string) => void;
  onRetry: () => void;
  onPage: (page: number) => void;
};

export function CompletedSalesList({
  items,
  list,
  loadState,
  fault,
  onSelect,
  onRetry,
  onPage,
}: CompletedSalesListProps) {
  return (
    <section aria-label="Tamamlanan etiket işleri">
      <header className="anl-sales-page-head">
        <div>
          <h1>Tamamlananlar</h1>
          <p>Baskı tamamlanma beyanı kaydedilmiş işler.</p>
        </div>
        <button
          type="button"
          className="anl-button is-text"
          disabled={loadState === "loading"}
          onClick={onRetry}
        >
          <RefreshCw size={14} aria-hidden="true" />
          Yenile
        </button>
      </header>
      {loadState === "loading" ? (
        <SystemState
          title="Tamamlanan işler yükleniyor"
          description="Erişiminiz kapsamındaki kayıtlar okunuyor."
          tone="loading"
        />
      ) : null}
      {loadState === "error" || loadState === "unauthorized" ? (
        <SystemState
          title="Tamamlanan işler alınamadı"
          description={fault?.message ?? "Oturumu kontrol edin."}
          tone="danger"
          primaryAction={{ label: "Yeniden dene", onClick: onRetry }}
        />
      ) : null}
      {loadState === "ready" && items.length === 0 ? (
        <SystemState
          title="Tamamlanan iş yok"
          description="Erişim kapsamınızda baskı tamamlanması kaydedilmiş bir iş bulunmuyor."
        />
      ) : null}
      {loadState === "ready" && items.length > 0 ? (
        <div
          className="anl-sales-task-table-wrap"
          role="region"
          aria-label="Tamamlanan işler tablosu"
          tabIndex={0}
        >
          <table className="anl-sales-task-table">
            <caption className="anl-visually-hidden">
              Tamamlanan etiket işleri
            </caption>
            <thead>
              <tr>
                <th>Hat / iş emri</th>
                <th>Müşteri / ürün</th>
                <th>Plan açıklaması</th>
                <th>Durum</th>
                <th aria-label="İşlem" />
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id}>
                  <td data-label="Hat / iş emri">
                    <strong>{item.lineName ?? "Hat belirtilmemiş"}</strong>
                    <span>{item.workOrder ?? "İş emri bekleniyor"}</span>
                    <small>{item.requestNo}</small>
                  </td>
                  <td data-label="Müşteri / ürün">
                    <strong>
                      {item.customerName ?? "Müşteri belirtilmemiş"}
                    </strong>
                    <span>{item.productName ?? "Ürün belirtilmemiş"}</span>
                  </td>
                  <td data-label="Plan açıklaması">
                    <span className="anl-sales-explanation-preview">
                      {item.planExplanation || "Açıklama yok"}
                    </span>
                  </td>
                  <td data-label="Durum">
                    <span className="anl-sales-task-state">
                      {stateLabels[item.state]}
                    </span>
                  </td>
                  <td data-label="İşlem">
                    <button
                      type="button"
                      className="anl-sales-primary"
                      onClick={() => onSelect(item.id)}
                    >
                      Kaydı aç
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
      {loadState === "ready" && list && list.total > list.pageSize ? (
        <div className="anl-sales-list-pages">
          <span>
            Sayfa {list.page} / {Math.ceil(list.total / list.pageSize)}
          </span>
          <button
            type="button"
            disabled={list.page <= 1}
            onClick={() => onPage(list.page - 1)}
          >
            Önceki
          </button>
          <button
            type="button"
            disabled={list.page * list.pageSize >= list.total}
            onClick={() => onPage(list.page + 1)}
          >
            Sonraki
          </button>
        </div>
      ) : null}
    </section>
  );
}
